import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import {
  Button,
  DataTable,
  EmptyState,
  FilterSelect,
  PageBody,
  Toast,
  type DataTableColumn,
} from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import { DateRangeFilter } from '@/components/DateRangeFilter.tsx';
import type { DateRangeOption } from '@/components/dateRangeOptions.ts';
import { CohortSearchSelect } from '@/components/pickers/CohortPicker.tsx';
import { useOrganisationFilter } from '@/lib/activeOrganisation.ts';
import { requireInternalOrganisation } from '@/lib/staffGate.ts';
import { useResetOnChange } from '@/lib/useResetOnChange.ts';
import type { CohortWithOrganisation } from '@/types/cohorts.ts';
import {
  downloadNpsResponsesCsv,
  fetchNpsBroadcasts,
  fetchNpsResponses,
  type NpsResponseFilters,
  type NpsResponseRow,
} from '@/lib/npsResponses.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';
import { formatDate, formatDateTime } from '@/utils/dateUtils.ts';
import { customAnswerText } from '@wundo/shared/customAnswerText.ts';

/* This page was `requiresSuperadminInternal` in the Vue router. */
export const Route = createFileRoute('/_app/nps-responses')({
  staticData: { orgScope: 'optional' },
  beforeLoad: ({ context }) => requireInternalOrganisation(context.queryClient),
  component: NpsResponsesPage,
});

/* This page's own presets: it reaches back further than a week's worth of logs. */
const RANGE_OPTIONS: readonly DateRangeOption[] = [
  { value: 'all', label: 'All time', days: null },
  { value: '30d', label: 'Last 30 days', days: 30 },
  { value: '90d', label: 'Last 90 days', days: 90 },
  { value: '180d', label: 'Last 6 months', days: 180 },
  { value: '365d', label: 'Last 12 months', days: 365 },
];

const ALL_BROADCASTS = '';

/* From midnight UTC, so a range means the same thing all day. */
function rangeStart(days: number | null): string | undefined {
  if (days === null) return undefined;

  const from = new Date();
  from.setUTCHours(0, 0, 0, 0);
  from.setUTCDate(from.getUTCDate() - days);
  return from.toISOString();
}

function customValue(row: NpsResponseRow, key: string): string {
  return customAnswerText(row.custom_answers?.[key]);
}

export function NpsResponsesPage() {
  const organisationId = useOrganisationFilter()?.id;
  const [cohort, setCohort] = useState<CohortWithOrganisation | null>(null);
  const [broadcastId, setBroadcastId] = useState<string>(ALL_BROADCASTS);
  const [range, setRange] = useState<string>(RANGE_OPTIONS[0].value);
  const [page, setPage] = useState(1);

  /* A cohort belongs to one organisation; a pick from the previous one would match nothing. */
  useResetOnChange(organisationId, () => {
    setCohort(null);
    setPage(1);
  });

  const filters = useMemo<NpsResponseFilters>(() => {
    const selected =
      RANGE_OPTIONS.find((option) => option.value === range) ?? RANGE_OPTIONS[0];

    return {
      organisation_id: organisationId,
      cohort_id: cohort?.id,
      broadcast_id: broadcastId || undefined,
      date_from: rangeStart(selected.days),
    };
  }, [organisationId, cohort, broadcastId, range]);

  const broadcasts = useQuery({
    queryKey: ['npsBroadcasts'],
    queryFn: fetchNpsBroadcasts,
  });

  const responses = useQuery({
    queryKey: ['npsResponses', filters, page],
    queryFn: () => fetchNpsResponses(filters, page),
    /* Rows stay put while the next page lands, which is what DataTable dims. */
    placeholderData: keepPreviousData,
    throwOnError: true,
  });

  const download = useMutation({ mutationFn: () => downloadNpsResponsesCsv(filters) });

  const rows = useMemo(() => responses.data?.data ?? [], [responses.data]);
  const total = responses.data?.meta.totalItems ?? 0;

  const broadcastOptions = useMemo(
    () => [
      { value: ALL_BROADCASTS, label: 'All broadcasts' },
      ...(broadcasts.data ?? []).map((broadcast) => ({
        value: broadcast.id,
        label: `${broadcast.skill_name} · ${formatDate(broadcast.scheduled_for)} (${broadcast.response_count})`,
      })),
    ],
    [broadcasts.data],
  );

  /* Every org-specific answer key present in the page becomes its own column. */
  const customKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const row of rows) {
      Object.keys(row.custom_answers ?? {}).forEach((key) => keys.add(key));
    }
    return [...keys].sort();
  }, [rows]);

  const columns = useMemo<DataTableColumn<NpsResponseRow>[]>(
    () => [
      /*
       * Every column is sized, so the table keeps its shape and scrolls
       * sideways instead of squeezing a date onto three lines — the same
       * arrangement the Vue table had, where every column held a floor and the
       * two long answers were given the room.
       */
      {
        key: 'created_at',
        header: 'Date',
        width: 'w-44',
        /* A timestamp broken over three lines is not a timestamp any more. */
        render: (row) => (
          <span className='whitespace-nowrap'>{formatDateTime(row.created_at)}</span>
        ),
      },
      { key: 'organisation_name', header: 'Organisation', width: 'w-48' },
      { key: 'user_name', header: 'User', width: 'w-44' },
      { key: 'user_email', header: 'Email', width: 'w-64' },
      { key: 'nps_score', header: 'NPS', width: 'w-16' },
      { key: 'nps_reason', header: 'NPS reason', width: 'w-72' },
      { key: 'conversation_quality_score', header: 'Conversations', width: 'w-32' },
      { key: 'time_saved', header: 'Time saved', width: 'w-32' },
      { key: 'time_saved_estimate', header: 'Time saved estimate', width: 'w-44' },
      { key: 'open_feedback', header: 'Open feedback', width: 'w-80' },
      ...customKeys.map((key) => ({
        key: `custom_${key}`,
        header: key,
        width: 'w-48',
        render: (row: NpsResponseRow) => customValue(row, key),
      })),
    ],
    [customKeys],
  );

  /* Any filter change starts the list again from its first page. */
  function applyFilter(change: () => void) {
    change();
    setPage(1);
  }

  return (
    <>
      <PageHeader title='NPS responses' />
      <PageBody>
        <DataTable<NpsResponseRow>
          label='NPS responses'
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          loading={responses.isFetching}
          pagination={{ page, pageSize: PAGE_SIZE, total }}
          onPageChange={setPage}
          emptyState={<EmptyState title='No NPS responses found' />}
          toolbar={
            <>
              <CohortSearchSelect
                className='w-56'
                placeholder='All cohorts'
                aria-label='Cohort'
                value={cohort}
                organisationId={organisationId}
                onChange={(next) => applyFilter(() => setCohort(next))}
              />
              <FilterSelect
                hideLabel
                label='Broadcast'
                options={broadcastOptions}
                value={broadcastId}
                onValueChange={(next) => applyFilter(() => setBroadcastId(next))}
              />
              <DateRangeFilter
                options={RANGE_OPTIONS}
                value={range}
                onChange={(next) => applyFilter(() => setRange(next))}
              />
              <Button
                variant='quiet'
                size='sm'
                disabled={rows.length === 0}
                loading={download.isPending}
                onClick={() => download.mutate()}
              >
                {download.isPending ? 'Preparing…' : 'Download CSV'}
              </Button>
            </>
          }
        />
      </PageBody>
      <Toast open={download.isError} message='Could not prepare the CSV' />
    </>
  );
}
